import { parseIsoDate } from '../../../common/ecxDateUtils';

export const WEB_BOOKING_TABS = [
    { id: 'new', label: 'New Requests', icon: 'bi-bell', status: 0, theme: 'cyan' },
    { id: 'followup', label: 'Follow Up', icon: 'bi-clock-history', status: 1, theme: 'warning' },
    { id: 'processed', label: 'Processed', icon: 'bi-check2-circle', status: 2, theme: 'success' },
    { id: 'all', label: 'All Requests', icon: 'bi-card-list', status: 5, theme: 'slate' },
];

export const WEB_BOOKING_SEARCH_COLUMNS = [
    { value: 'firstName', label: 'First Name', icon: 'bi-person', theme: 'indigo' },
    { value: 'lastName', label: 'Last Name', icon: 'bi-person-badge', theme: 'purple' },
    { value: 'preferredName', label: 'Preferred Name', icon: 'bi-person-heart', theme: 'pink' },
    { value: 'cellPhone', label: 'Cell Phone', icon: 'bi-phone', theme: 'success' },
    { value: 'homePhone', label: 'Home Phone', icon: 'bi-telephone', theme: 'cyan' },
    { value: 'email', label: 'Email', icon: 'bi-envelope', theme: 'pink' },
];

export const DEFAULT_WEB_BOOKING_SEARCH = {
    column: 'firstName',
    value: '',
    fromDate: '',
    toDate: '',
};

/**
 * Format a calendar date (YYYY-MM-DD from LocalDate) without UTC shift.
 * `new Date('2025-10-27')` is UTC midnight and shows the previous day in US timezones.
 */
export const formatBookingDate = (dateString) => {
    if (!dateString) {
        return '—';
    }

    const date = parseIsoDate(String(dateString).slice(0, 10));
    if (!date) {
        return '—';
    }

    return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

export const formatBookingDateTime = (dateString) => {
    if (!dateString) {
        return '—';
    }

    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

export const getPatientInitials = (firstName = '', lastName = '') =>
    `${firstName.charAt(0) || ''}${lastName.charAt(0) || ''}`.toUpperCase() || '?';

export const getBookingStatusMeta = (status) => {
    const config = {
        0: { className: 'web-bookings-status web-bookings-status--new', icon: 'bi-bell', label: 'New' },
        1: { className: 'web-bookings-status web-bookings-status--followup', icon: 'bi-clock-history', label: 'Follow Up' },
        2: { className: 'web-bookings-status web-bookings-status--processed', icon: 'bi-check2-circle', label: 'Processed' },
    };

    return config[status] || config[0];
};

export const getPatientTypeMeta = (isNewPatient) =>
    isNewPatient
        ? { className: 'web-bookings-patient-type web-bookings-patient-type--new', label: 'New Patient' }
        : { className: 'web-bookings-patient-type web-bookings-patient-type--existing', label: 'Existing Patient' };

export const hasWebBookingSearchFilters = (search = {}) =>
    Boolean(search.value?.trim() || search.fromDate || search.toDate);

/**
 * Date filter rules:
 * - only fromDate (or only toDate) → that single calendar day
 * - both fromDate and toDate → inclusive range
 */
export const resolveBookingDateRange = (search = {}) => {
    const fromDate = search.fromDate || '';
    const toDate = search.toDate || '';

    if (!fromDate && !toDate) {
        return {};
    }

    if (fromDate && toDate) {
        return { fromDate, toDate };
    }

    const singleDay = fromDate || toDate;
    return { fromDate: singleDay, toDate: singleDay };
};

export const buildBookingRequestParams = (bookingStatus, search) => ({
    status: bookingStatus,
    ...(search.value?.trim()
        ? { column: search.column, value: search.value.trim() }
        : {}),
    ...resolveBookingDateRange(search),
});

/** @deprecated Use buildBookingRequestParams with bookingService.getBookingRequests */
export const buildBookingSearchQuery = (bookingStatus, search) => {
    const params = buildBookingRequestParams(bookingStatus, search);
    const query = new URLSearchParams({ status: String(params.status) });

    if (params.column && params.value) {
        query.set('column', params.column);
        query.set('value', params.value);
    }
    if (params.fromDate) {
        query.set('fromDate', params.fromDate);
    }
    if (params.toDate) {
        query.set('toDate', params.toDate);
    }

    return query.toString();
};
